// Uploads in pieces (owner, 2026-10-23: large files, slow and dropped connections). upload(file) sends a file to
// UploadController 5 MB at a time, sends a dropped piece again from where it stopped (growing pauses, ~10 tries), and
// resolves with the signed name of the whole file, which the server then takes: a Livewire component
// (Component::finishChunkedUpload), the editor (media.attach). It rejects with an UploadError (the server's reason,
// shown as it is) or another Error (the connection).
//
// Every file field bound with wire:model is taken over before Livewire sees it: the field hears what it would from
// Livewire, livewire-upload-start, -progress (detail.progress), -finish and -error (detail.message, when there's one).

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content ?? '';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class UploadError extends Error {}

/** Sends `file`; progress(percent) as it goes. Resolves with its signed name. */
export async function upload(file, {progress = () => {}} = {}) {
    const {id} = await json('/upload', {method: 'POST', body: JSON.stringify({name: file.name, size: file.size})});
    let received = 0, path = null;

    while (path === null) {
        ({received, path} = await piece(id, file, received, (sent) => progress(Math.floor(sent * 100 / file.size))));
    }

    return path;
}

document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file' || input.multiple || !input.files?.length) return;
    const model = input.getAttributeNames().find((name) => name.startsWith('wire:model'));
    const component = input.closest('[wire\\:id]');
    if (!model || !component) return;

    event.stopImmediatePropagation();
    field(input, Livewire.find(component.getAttribute('wire:id')), input.getAttribute(model), input.files[0]);
}, true);

async function field(input, $wire, name, file) {
    const emit = (type, detail = {}) => input.dispatchEvent(new CustomEvent(`livewire-upload-${type}`, {bubbles: true, detail}));
    emit('start');

    try {
        await $wire.call('finishChunkedUpload', name, await upload(file, {progress: (progress) => emit('progress', {progress})}));
        emit('finish');
    } catch (error) {
        emit('error', {message: error instanceof UploadError ? error.message : null});
    } finally {
        input.value = '';   // choosing the same file again is a change
    }
}

// The next piece from `offset`; after a dropped connection, asks how much arrived and carries on from there
async function piece(id, file, offset, progress) {
    for (let attempt = 0; ; attempt++) {
        try {
            return await send(id, file.slice(offset, offset + 5 * 1024 * 1024), offset, progress);
        } catch (error) {
            if (error instanceof UploadError || attempt >= 9) throw error;
            await wait(Math.min(1000 * 2 ** attempt, 30000));
            try {
                offset = (await json(`/upload/${id}`)).received;
            } catch (error) {
                if (error instanceof UploadError) throw error;   // still offline: the next try asks again
            }
        }
    }
}

function send(id, chunk, offset, progress) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('POST', `/upload/${id}`);
        request.setRequestHeader('Accept', 'application/json');
        request.setRequestHeader('Content-Type', 'application/octet-stream');
        request.setRequestHeader('X-CSRF-TOKEN', csrf());
        request.setRequestHeader('Upload-Offset', offset);
        request.upload.addEventListener('progress', (event) => progress(offset + event.loaded));
        request.addEventListener('load', () => {
            if (request.status === 200) return resolve(JSON.parse(request.response));
            // Refused (too large, not a file, not this sender's): no point trying again
            if ([403, 404, 413, 419, 422].includes(request.status)) return reject(new UploadError(message(request.response)));
            reject(new Error(String(request.status)));   // the wrong offset (409), or the server busy: ask, then again
        });
        request.addEventListener('error', () => reject(new Error('network')));
        request.send(chunk);
    });
}

/** A JSON request to the site, with its CSRF token; a refusal rejects with the server's reason as an UploadError */
export async function json(url, options = {}) {
    let response;
    try {
        response = await fetch(url, {...options, headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf()}});
    } catch {
        throw new Error('network');
    }
    // Refused, or sent elsewhere (signed out: the sign-in page): the server's reason, else a plain one
    if (!response.ok || response.redirected) throw new UploadError(message(await response.text()) ?? 'That could not be added.');
    return response.json();
}

function message(body) {
    try {
        const data = JSON.parse(body);
        return Object.values(data.errors ?? {})[0]?.[0] ?? data.message ?? null;
    } catch {
        return null;
    }
}
